import { useMemo, useState } from 'react';
import type { UseLazyIssueTypeFieldsArguments } from '../../services/types.tsx';
import { useLazyIssueTypesFieldsMap } from '../use-lazy-issue-types-fields-map/index.tsx';

/**
 * This hook exists mainly to isolate the duplicated states for `selectedCardTypeId` between the inner ICC Form and the ICC wrapper, where this hook is meant to be used.
 * The settter `setSelectedCardTypeId` shouldn't be called anywhere else other than the ICC Form to keep state consistency.
 *
 * @returns {object} - An object containing the following properties:
 * - `fetchFields`: A function to fetch the issue types and fields. This must be called before ICC is opened.
 * - `iccProps`: An object containing properties which can be passed to the ICC component directly, or used to build new props, including:
 *   - - `shouldShowDueDatePicker`: A boolean indicating if the due date picker should be shown.
 *   - - `shouldShowAssigneePicker`: A boolean indicating if the assignee picker should be shown.
 *   - - `onChangeCardType`: A function to capture the internal state of the selected card type ID.
 */
export const useLazyIssueTypesAndFieldsForICC = ({
	projectKey,
}: UseLazyIssueTypeFieldsArguments) => {
	const [selectedCardTypeId, setSelectedCardTypeId] = useState<string | number | null>(null);
	const fieldsUtil = useLazyIssueTypesFieldsMap({
		projectKey,
	});
	return {
		fetchFields: fieldsUtil.load,
		iccProps: useMemo(
			() => ({
				shouldShowDueDatePicker: fieldsUtil.checkIssueTypeHasField(selectedCardTypeId, 'duedate'),
				shouldShowAssigneePicker: fieldsUtil.checkIssueTypeHasField(selectedCardTypeId, 'assignee'),
				onChangeCardType: setSelectedCardTypeId,
			}),
			[fieldsUtil, selectedCardTypeId],
		),
	};
};
